import React, { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { Calendar, MapPin, Clock, FileText, Camera, Trash2 } from 'lucide-react';

const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL || '',
  import.meta.env.VITE_SUPABASE_ANON_KEY || ''
);

interface Visit {
  id: string;
  location_id: string;
  scheduled_date: string;
  status: 'scheduled' | 'completed' | 'cancelled';
  notes: string;
  photos: string[];
  location_analysis: {
    location: string;
    business_type: string;
  };
}

const VisitSchedulePage: React.FC = () => {
  const [visits, setVisits] = useState<Visit[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<string>('');
  const [notes, setNotes] = useState('');
  const [photos, setPhotos] = useState<string[]>([]);

  useEffect(() => {
    loadVisits();
  }, []);

  const loadVisits = async () => {
    try {
      const { data, error } = await supabase
        .from('property_visits')
        .select(`
          *,
          location_analysis (
            location,
            business_type
          )
        `)
        .order('scheduled_date', { ascending: true });

      if (error) throw error;
      setVisits(data || []);
    } catch (error) {
      console.error('Error loading visits:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDateSelect = (selectInfo: any) => {
    setSelectedDate(selectInfo.start);
  };

  const handleScheduleVisit = async () => {
    if (!selectedDate || !selectedLocation) return;

    try {
      const { data, error } = await supabase
        .from('property_visits')
        .insert([
          {
            location_id: selectedLocation,
            scheduled_date: selectedDate.toISOString(),
            notes,
            photos
          }
        ])
        .select(`
          *,
          location_analysis (
            location,
            business_type
          )
        `)
        .single();

      if (error) throw error;

      setVisits([...visits, data]);
      setSelectedDate(null);
      setSelectedLocation('');
      setNotes('');
      setPhotos([]);
    } catch (error) {
      console.error('Error scheduling visit:', error);
    }
  };

  const handleDeleteVisit = async (visitId: string) => {
    try {
      const { error } = await supabase
        .from('property_visits')
        .delete()
        .eq('id', visitId);

      if (error) throw error;

      setVisits(visits.filter(visit => visit.id !== visitId));
    } catch (error) {
      console.error('Error deleting visit:', error);
    }
  };

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    // In a real app, you would upload these to storage
    // For now, we'll just store the file names
    const newPhotos = Array.from(files).map(file => file.name);
    setPhotos([...photos, ...newPhotos]);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <div className="bg-card/50 backdrop-blur-sm border border-border rounded-lg p-6">
        <h2 className="text-2xl font-bold mb-6 flex items-center">
          <Calendar className="mr-2 text-primary" />
          Visit Schedule
        </h2>

        <div className="mb-8">
          <FullCalendar
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            selectable={true}
            select={handleDateSelect}
            events={visits.map(visit => ({
              id: visit.id,
              title: visit.location_analysis.location,
              start: visit.scheduled_date,
              className: `status-${visit.status}`
            }))}
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right: 'dayGridMonth,timeGridWeek,timeGridDay'
            }}
          />
        </div>

        {selectedDate && (
          <div className="bg-card border border-border rounded-lg p-4 space-y-4">
            <h3 className="font-semibold">Schedule New Visit</h3>
            
            <div className="space-y-2">
              <label className="block text-sm font-medium">Location</label>
              <select
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
                className="w-full p-2 bg-card border border-border rounded-lg"
              >
                <option value="">Select a location</option>
                {/* Add location options */}
              </select>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium">Notes</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full p-2 bg-card border border-border rounded-lg h-32"
                placeholder="Add any notes about the visit..."
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium">Photos</label>
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={handlePhotoUpload}
                className="w-full p-2 bg-card border border-border rounded-lg"
              />
            </div>

            <button
              onClick={handleScheduleVisit}
              className="w-full bg-primary text-primary-foreground py-2 rounded-lg hover:bg-primary/90 transition-colors"
            >
              Schedule Visit
            </button>
          </div>
        )}

        <div className="mt-8">
          <h3 className="font-semibold mb-4">Upcoming Visits</h3>
          <div className="space-y-4">
            {visits.map(visit => (
              <div
                key={visit.id}
                className="border border-border rounded-lg p-4"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-medium">{visit.location_analysis.location}</h4>
                    <p className="text-sm text-muted-foreground">
                      {visit.location_analysis.business_type}
                    </p>
                    <div className="mt-2 space-y-1 text-sm">
                      <p className="flex items-center">
                        <Clock size={16} className="mr-1" />
                        {new Date(visit.scheduled_date).toLocaleString()}
                      </p>
                      <p className="flex items-center">
                        <FileText size={16} className="mr-1" />
                        {visit.notes || 'No notes'}
                      </p>
                      {visit.photos.length > 0 && (
                        <p className="flex items-center">
                          <Camera size={16} className="mr-1" />
                          {visit.photos.length} photos
                        </p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeleteVisit(visit.id)}
                    className="text-destructive hover:text-destructive/90"
                  >
                    <Trash2 size={20} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VisitSchedulePage;